/**
 * Somebody's time in lieu on their page (Employees → a person): the balance, the history,
 * "Record time in lieu" for whoever may sign off their claims (B, assumption 7) and a
 * correction for admins (G). Asks the server who may do what (`can_record`, `can_correct`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Plus, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { toilApi } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import ClaimToilDialog from './ClaimToilDialog';
import CorrectionForm from './CorrectionForm';
import ToilHistory from './ToilHistory';

export default function ToilPanel({ userId, userName }) {
  const [data, setData] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const load = useCallback(() => {
    toilApi.forUser(userId).then((r) => setData(r.data)).catch(() => setData(null));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (!data || (!data.enabled && !data.summary?.ever)) return null;
  const s = data.summary || {};
  return (
    <div className="card-hive p-5" data-testid="toil-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-teal-500" /> Time in lieu</h3>
          <p className="text-sm text-muted-foreground">
            <b className={`text-lg font-heading ${s.to_use_minutes < 0 ? 'text-red-600' : 'text-foreground'}`}>{s.to_use}</b> to use
            {s.waiting_claims > 0 && <> · {s.waiting} waiting for sign-off</>}
            {s.soonest && <> · {s.soonest.amount} must be used by {formatDateGB(s.soonest.use_by)}</>}
          </p>
        </div>
        <div className="flex gap-2">
          {data.can_record && (
            <Button type="button" size="sm" className="btn-primary" onClick={() => setRecordOpen(true)} data-testid="toil-record-btn">
              <Plus className="w-4 h-4 mr-1" /> Record time in lieu
            </Button>
          )}
          {data.can_correct && !correcting && (
            <Button type="button" size="sm" variant="outline" onClick={() => setCorrecting(true)} data-testid="toil-correct-btn">
              <Wrench className="w-4 h-4 mr-1" /> Correction
            </Button>
          )}
        </div>
      </div>
      {correcting && (
        <div className="mb-4">
          <CorrectionForm userId={userId} onDone={() => { setCorrecting(false); load(); }} onCancel={() => setCorrecting(false)} />
        </div>
      )}
      <ToilHistory lines={data.history} />
      <ClaimToilDialog open={recordOpen} onOpenChange={setRecordOpen} onDone={load} withRate
        forUser={{ id: userId, name: data.name || userName }} usualRate={data.settings?.default_rate} />
    </div>
  );
}
